"use client";

import React, { useEffect, useState, useRef } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Trash2, Loader2, CheckCircle2, AlertCircle, FolderOpen, FileCheck, UserPlus, FileText, Menu, Upload } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../ui/HoverCard";
import { Checkbox } from "../ui/checkbox";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import { useAuth } from "@/contexts/AuthContext";
import { useEngagement } from "@/contexts/EngagementContext";
import { apiService } from "@/services/api-service";
import { RenameFileDialog } from "./RenameFileDialog";

type SharePointDoc = {
  name: string;
  url: string;
  reference?: string;
};

type SignoffEntry = {
  name: string;
  prep_so: Array<{ name: string; metadata: string }>;
  rev_so: Array<{ name: string; metadata: string }>;
  conc_so: Array<{ name: string; metadata: string }>;
  eqr_so: Array<{ name: string; metadata: string }>;
  partner_so: Array<{ name: string; metadata: string }>;
};

type SignoffData = {
  signoff: SignoffEntry[];
};

type AssignDocumentData = {
  data: {
    [fileName: string]: string[];
  };
};

type TeamMember = {
  id: string;
  user_name?: string;
  email?: string;
};

// Helper function to get initials from a name
const getInitials = (name: string): string => {
  if (!name || name.trim() === "") return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const formatSignoffDate = (dateString: string) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    return date.toLocaleString();
  } catch {
    return dateString;
  }
};

const StaticSignoffGroup = ({
  label,
  entries,
}: {
  label: string;
  entries: Array<{ name: string; metadata: string }>;
}) => {
  const validEntries = entries.filter((entry) => entry.name && entry.name.trim() !== "");
  if (validEntries.length === 0) return null;

  return (
    <HoverCard openDelay={150} closeDelay={100}>
      <HoverCardTrigger asChild>
        <div className="flex items-center gap-0.5 cursor-help">
          {validEntries.slice(0, 3).map((entry, i) => (
            <div
              key={i}
              className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
              title={`${entry.name} - ${formatSignoffDate(entry.metadata)}`}
            >
              {getInitials(entry.name)}
            </div>
          ))}
          {validEntries.length > 3 && (
            <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
              +{validEntries.length - 3}
            </div>
          )}
        </div>
      </HoverCardTrigger>
      <HoverCardContent className="w-72 p-3">
        <div className="font-semibold mb-1 text-white/90">
          {label} ({validEntries.length})
        </div>
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {validEntries.map((entry, idx) => (
            <div key={idx} className="border-b border-white/10 pb-1 last:border-b-0 last:pb-0">
              <div className="font-medium text-xs text-white/90">{entry.name}</div>
              {entry.metadata && (
                <div className="text-white/70 text-[10px]">{formatSignoffDate(entry.metadata)}</div>
              )}
            </div>
          ))}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};

// Component to display signoff badge with hover tooltip and signoff button
const SignoffBadge = ({ 
  label, 
  signoffArray, 
  isEmpty,
  onSignoffClick,
  disabled = false,
  section
}: { 
  label: string; 
  signoffArray: Array<{ name: string; metadata: string }>; 
  isEmpty: boolean;
  onSignoffClick?: () => void;
  disabled?: boolean;
  section: 'prep_so' | 'rev_so' | 'conc_so' | 'eqr_so' | 'partner_so';
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  // Filter out empty entries
  const validEntries = signoffArray.filter(entry => entry.name && entry.name.trim() !== "");
  const hasEntries = validEntries.length > 0;
  
  // Format date for display
  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  };

  return (
    <div className="relative inline-flex items-center gap-1">
      {/* Display signoff entries */}
      {hasEntries && (
        <div 
          className="flex flex-col items-center gap-1"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          {validEntries.slice(0, 3).map((entry, idx) => {
            const initials = getInitials(entry.name);
            return (
              <div
                key={idx}
                className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium cursor-help border border-blue-500/30"
                title={`${entry.name} - ${formatDate(entry.metadata)}`}
              >
                {initials}
              </div>
            );
          })}
          {validEntries.length > 3 && (
            <div className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium cursor-help border border-blue-500/30">
              +{validEntries.length - 3}
            </div>
          )}
        </div>
      )}
      
      {/* Always show signoff button */}
      <Button
        size="sm"
        variant="ghost"
        onClick={onSignoffClick}
        disabled={disabled}
        className={`h-6 w-6 p-0 ${disabled ? 'text-white/30 cursor-not-allowed' : 'text-blue-400 hover:text-blue-300 hover:bg-blue-500/10'}`}
        title={disabled ? `You don't have permission to add ${label} Signoff` : `Add ${label} Signoff`}
      >
        <FileCheck className="h-3 w-3" />
      </Button>
      
      {/* Tooltip showing all entries */}
      {showTooltip && hasEntries && (
        <div className="absolute z-10 px-2 py-1 text-xs text-white bg-gray-900 rounded shadow-lg border border-white/10 bottom-full mb-2 left-1/2 transform -translate-x-1/2 min-w-[180px] max-w-md">
          <div className="font-semibold mb-1">{label} ({validEntries.length})</div>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {validEntries.map((entry, idx) => (
              <div key={idx} className="border-b border-white/10 pb-1 last:border-b-0 last:pb-0">
                <div className="font-medium">{entry.name}</div>
                {entry.metadata && (
                  <div className="text-white/70 text-[10px]">{formatDate(entry.metadata)}</div>
                )}
              </div>
            ))}
          </div>
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

// Component to display assign document button with hover tooltip
const AssignDocumentBadge = ({
  assignedUsers,
  disabled = false,
  loading = false,
  children,
}: {
  assignedUsers: string[];
  disabled?: boolean;
  loading?: boolean;
  children: React.ReactNode;
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const hasAssignedUsers = assignedUsers.length > 0;

  return (
    <div 
      className="relative inline-flex items-center"
      onMouseEnter={() => hasAssignedUsers && setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {children}
      
      {/* Tooltip showing assigned users */}
      {showTooltip && hasAssignedUsers && (
        <div className="absolute z-10 px-2 py-1 text-xs text-white bg-gray-900 rounded shadow-lg border border-white/10 bottom-full mb-2 left-1/2 transform -translate-x-1/2 min-w-[180px] max-w-md">
          <div className="font-semibold mb-1">Assigned Users ({assignedUsers.length})</div>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {assignedUsers.map((userName, idx) => (
              <div key={idx} className="border-b border-white/10 pb-1 last:border-b-0 last:pb-0">
                <div className="font-medium">{userName}</div>
              </div>
            ))}
          </div>
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

interface DocumentsPageProps {
  filteredDocs?: SharePointDoc[];
  isLoading?: boolean;
  title?: string;
  description?: string;
  showUploadButton?: boolean;
  /** When true, do not render the page title/description (e.g. when embedded in LibrariesPage with its own header). */
  hideHeader?: boolean;
}

export default function DocumentsPage({ 
  filteredDocs, 
  isLoading: externalLoading, 
  title = "Document Library",
  description = "Upload and manage supporting documents",
  showUploadButton = true,
  hideHeader = false,
}: DocumentsPageProps = {}) {
  const { isArchivalMode } = useArchivalMode();
  const { user } = useAuth();
  const { currentEngagement } = useEngagement();
  const [docs, setDocs] = useState<SharePointDoc[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadStatus, setUploadStatus] = useState<{ success: boolean; message: string } | null>(null);
  const [signoffData, setSignoffData] = useState<SignoffData | null>(null);
  const [loadingSignoff, setLoadingSignoff] = useState<boolean>(false);
  const [userEngagementRole, setUserEngagementRole] = useState<string | null>(null);
  const [loadingRole, setLoadingRole] = useState<boolean>(false);
  const [assignDocumentData, setAssignDocumentData] = useState<AssignDocumentData | null>(null);
  const [loadingAssignDocument, setLoadingAssignDocument] = useState<boolean>(false);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loadingTeamMembers, setLoadingTeamMembers] = useState<boolean>(false);
  const [selectedDocNames, setSelectedDocNames] = useState<Set<string>>(new Set());
  const [deletingSelected, setDeletingSelected] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [fileToRename, setFileToRename] = useState<{ name: string; area: string } | null>(null);
  const [replacingFile, setReplacingFile] = useState<{ name: string; area: string } | null>(null);

  const toggleDocSelection = (name: string) => {
    setSelectedDocNames((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const selectAllDocs = () => {
    const uniqueNames = docs.filter((d, i, self) => self.findIndex((x) => x.name === d.name) === i).map((d) => d.name);
    const allSelected = uniqueNames.length > 0 && uniqueNames.every((n) => selectedDocNames.has(n));
    setSelectedDocNames(allSelected ? new Set() : new Set(uniqueNames));
  };

  const loadDocs = async () => {
    // If filteredDocs is provided, use it instead of loading
    // Check if it's explicitly provided (not undefined) - empty array is valid
    if (filteredDocs !== undefined) {
      setDocs(filteredDocs);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");
      const res = await (window as any)?.sharePointAPI?.loadAllDocuments?.();
      if (res?.success) {
        setDocs(Array.isArray(res.data) ? res.data : []);
      } else {
        setError(res?.error || "Failed to load document list");
      }
    } catch (e: any) {
      setError(e?.message || "Unexpected error while loading document list");
    } finally {
      setLoading(false);
    }
  };

  const loadSignoffData = async () => {
    try {
      setLoadingSignoff(true);
      const res = await (window as any)?.sharePointAPI?.downloadSignoffJson?.();
      if (res?.success) {
        setSignoffData(res.data || { signoff: [] });
      } else {
        // If Signoff.json doesn't exist or fails to load, use empty structure
        setSignoffData({ signoff: [] });
      }
    } catch (e: any) {
      console.error("Error loading signoff data:", e);
      setSignoffData({ signoff: [] });
    } finally {
      setLoadingSignoff(false);
    }
  };

  const loadAssignDocumentData = async () => {
    try {
      setLoadingAssignDocument(true);
      const res = await (window as any)?.sharePointAPI?.downloadAssignDocumentJson?.();
      if (res?.success) {
        setAssignDocumentData(res.data || { data: {} });
      } else {
        // If AssignDocument.json doesn't exist or fails to load, use empty structure
        setAssignDocumentData({ data: {} });
      }
    } catch (e: any) {
      console.error("Error loading assign document data:", e);
      setAssignDocumentData({ data: {} });
    } finally {
      setLoadingAssignDocument(false);
    }
  };

  const loadTeamMembers = async () => {
    if (!currentEngagement) {
      setTeamMembers([]);
      return;
    }

    try {
      setLoadingTeamMembers(true);
      const team = await apiService.getEngagementTeam(currentEngagement.id);
      // Normalize team members structure
      const normalizedTeam = team.map((member: any) => ({
        id: member.id,
        user_name: member.user_name || `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email,
        email: member.email,
      }));
      setTeamMembers(normalizedTeam);
    } catch (error) {
      console.error('Error loading team members:', error);
      setTeamMembers([]);
    } finally {
      setLoadingTeamMembers(false);
    }
  };

  // Get signoff entry for a specific file
  const getSignoffForFile = (fileName: string): SignoffEntry | null => {
    if (!signoffData || !signoffData.signoff) return null;
    return signoffData.signoff.find((entry) => entry.name === fileName) || null;
  };

  // Get assigned users for a specific file
  const getAssignedUsersForFile = (fileName: string): string[] => {
    if (!assignDocumentData || !assignDocumentData.data) return [];
    return assignDocumentData.data[fileName] || [];
  };

  // Load user's engagement role
  const loadUserEngagementRole = async () => {
    if (!currentEngagement || !user) {
      setUserEngagementRole(null);
      return;
    }

    try {
      setLoadingRole(true);
      const team = await apiService.getEngagementTeam(currentEngagement.id);
      const currentUserMember = team.find((member: any) => member.id === user.id);
      const role = currentUserMember?.role || null;
      setUserEngagementRole(role);
    } catch (error) {
      console.error("Error loading user engagement role:", error);
      setUserEngagementRole(null);
    } finally {
      setLoadingRole(false);
    }
  };

  // Map roles to signoff sections
  const canSignoffSection = (section: 'prep_so' | 'rev_so' | 'conc_so' | 'eqr_so' | 'partner_so'): boolean => {
    if (!user) return false;

    const engagementRole = userEngagementRole;
    const userType = user.type; // 'partner', 'manager', 'associate', 'article'

    // Check if user's role allows this section
    switch (section) {
      case 'prep_so':
        // associate, article can do prep_so (check both engagement role and user type)
        return (engagementRole === 'associate' || engagementRole === 'article') ||
               (userType === 'associate' || userType === 'article');
      
      case 'rev_so':
        // engagement_manager, manager, associate can do rev_so
        return engagementRole === 'engagement_manager' ||
               userType === 'manager' ||
               userType === 'associate' ||
               engagementRole === 'associate';
      
      case 'conc_so':
        // manager, partner can do conc_so (check user type)
        return userType === 'manager' || userType === 'partner';
      
      case 'eqr_so':
        // eqr_partner, eqr_manager can do eqr_so (check engagement role)
        return engagementRole === 'eqr_partner' || engagementRole === 'eqr_manager';
      
      case 'partner_so':
        // engagement_partner can do partner_so (check engagement role)
        return engagementRole === 'engagement_partner';
      
      default:
        return false;
    }
  };

  // Check if prep_so is completed for a file
  const isPrepSoCompleted = (signoffEntry: SignoffEntry | null): boolean => {
    if (!signoffEntry) return false;
    return signoffEntry.prep_so && 
           signoffEntry.prep_so.some(entry => entry.name && entry.name.trim() !== "");
  };

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      // Always load signoff and assign data (needed for functionality)
      await Promise.all([loadSignoffData(), loadAssignDocumentData()]);
      await loadUserEngagementRole();
      await loadTeamMembers();
      
      // Only load all documents if filteredDocs is NOT provided
      if (filteredDocs === undefined) {
        await loadDocs();
      }
    };
    run();
    return () => {
      mounted = false;
    };
  }, [currentEngagement, user]);

  // Update docs when filteredDocs changes (this handles the filtered case)
  useEffect(() => {
    if (filteredDocs !== undefined) {
      // Deduplicate by name to ensure no duplicates
      const uniqueDocsMap = new Map<string, SharePointDoc>();
      filteredDocs.forEach((doc: SharePointDoc) => {
        if (doc.name && !uniqueDocsMap.has(doc.name)) {
          uniqueDocsMap.set(doc.name, doc);
        }
      });
      setDocs(Array.from(uniqueDocsMap.values()));
    }
  }, [filteredDocs]);

  // Reload role when engagement or user changes
  useEffect(() => {
    if (currentEngagement && user) {
      loadUserEngagementRole();
    } else {
      setUserEngagementRole(null);
    }
  }, [currentEngagement?.id, user?.id]);

  const handleSignoff = async (fileName: string, section: 'prep_so' | 'rev_so' | 'conc_so' | 'eqr_so' | 'partner_so') => {
    if (!user || !user.user_name) {
      setError("User information not available");
      return;
    }

    try {
      setLoadingSignoff(true);
      setError("");

      const result = await (window as any)?.sharePointAPI?.uploadSignoff?.({
        fileName: fileName,
        section: section,
        userName: user.user_name
      });

      if (result?.success) {
        // Reload signoff data to reflect the update
        await loadSignoffData();
        setUploadStatus({
          success: true,
          message: `Signoff updated successfully for ${fileName}`
        });
        // Clear status message after 3 seconds
        setTimeout(() => {
          setUploadStatus(null);
        }, 3000);
      } else {
        setError(result?.error || "Failed to update signoff");
        setUploadStatus({
          success: false,
          message: result?.error || "Failed to update signoff"
        });
      }
    } catch (e: any) {
      const errorMessage = e?.message || "Unexpected error while updating signoff";
      setError(errorMessage);
      setUploadStatus({
        success: false,
        message: errorMessage
      });
    } finally {
      setLoadingSignoff(false);
    }
  };

  const handleBulkSignoff = async (section: 'prep_so' | 'rev_so' | 'conc_so' | 'eqr_so' | 'partner_so') => {
    if (!user?.user_name || selectedDocNames.size === 0) return;
    const names = Array.from(selectedDocNames);
    try {
      setLoadingSignoff(true);
      setError("");
      let successCount = 0;
      let lastError: string | null = null;
      for (const fileName of names) {
        const result = await (window as any)?.sharePointAPI?.uploadSignoff?.({
          fileName,
          section,
          userName: user.user_name,
        });
        if (result?.success) successCount++;
        else if (result?.error) lastError = result.error;
      }
      await loadSignoffData();
      setSelectedDocNames(new Set());
      if (successCount < names.length) {
        if (successCount === 0) {
          setUploadStatus({ success: false, message: lastError || "Sign-off failed for all selected documents." });
        } else {
          setUploadStatus({ success: false, message: `Sign-off applied to ${successCount} of ${names.length}. ${lastError || ""}` });
        }
        setTimeout(() => setUploadStatus(null), 4000);
      }
    } catch (e: any) {
      setUploadStatus({ success: false, message: e?.message || "Bulk sign-off failed." });
      setTimeout(() => setUploadStatus(null), 4000);
    } finally {
      setLoadingSignoff(false);
    }
  };

  const handleAssignDocument = async (fileName: string, userName: string) => {
    if (!fileName || !userName) {
      setError("File name and user name are required");
      return;
    }

    try {
      setLoadingAssignDocument(true);
      setError("");

      const result = await (window as any)?.sharePointAPI?.uploadAssignDocument?.({
        fileName: fileName,
        userName: userName
      });

      if (result?.success) {
        // Reload assign document data to reflect the update
        await loadAssignDocumentData();
        setUploadStatus({
          success: true,
          message: `Document assigned successfully to ${userName}`
        });
        // Clear status message after 3 seconds
        setTimeout(() => {
          setUploadStatus(null);
        }, 3000);
      } else {
        setError(result?.error || "Failed to assign document");
        setUploadStatus({
          success: false,
          message: result?.error || "Failed to assign document"
        });
      }
    } catch (e: any) {
      const errorMessage = e?.message || "Unexpected error while assigning document";
      setError(errorMessage);
      setUploadStatus({
        success: false,
        message: errorMessage
      });
    } finally {
      setLoadingAssignDocument(false);
    }
  };

  const handleBulkAssignDocument = async (userName: string) => {
    if (!userName.trim() || selectedDocNames.size === 0) return;
    const names = Array.from(selectedDocNames);
    try {
      setLoadingAssignDocument(true);
      setError("");
      let successCount = 0;
      let lastError: string | null = null;
      for (const fileName of names) {
        const result = await (window as any)?.sharePointAPI?.uploadAssignDocument?.({
          fileName,
          userName: userName.trim(),
        });
        if (result?.success) successCount++;
        else if (result?.error) lastError = result.error;
      }
      await loadAssignDocumentData();
      setSelectedDocNames(new Set());
      if (successCount === names.length) {
        setUploadStatus({ success: true, message: `${successCount} document(s) assigned to ${userName.trim()}.` });
      } else if (successCount > 0) {
        setUploadStatus({ success: false, message: `Assigned ${successCount} of ${names.length} to ${userName.trim()}. ${lastError || ""}` });
      } else {
        setUploadStatus({ success: false, message: lastError || "Assign failed for all selected documents." });
      }
      setTimeout(() => setUploadStatus(null), 4000);
    } catch (e: any) {
      setUploadStatus({ success: false, message: e?.message || "Bulk assign failed." });
      setTimeout(() => setUploadStatus(null), 4000);
    } finally {
      setLoadingAssignDocument(false);
    }
  };

  const handleDelete = async (fileName: string) => {
    if (!fileName) {
      setError("File name is required");
      return;
    }

    // Confirm deletion
    if (!confirm(`Are you sure you want to delete "${fileName}"? This will move it to the recycle bin.`)) {
      return;
    }

    try {
      setLoading(true);
      setError("");
      const result = await (window as any)?.sharePointAPI?.deleteDocumentLibrary?.(fileName);
      
      if (result?.success) {
        // Also remove the file entry from AssignDocument.json
        try {
          const removeResult = await (window as any)?.sharePointAPI?.removeAssignDocument?.(fileName);
          if (removeResult?.success) {
            console.log("Removed file entry from AssignDocument.json", { fileName });
          } else {
            // Log error but don't fail the delete operation
            console.warn("Failed to remove file entry from AssignDocument.json:", removeResult?.error);
          }
        } catch (removeError) {
          // Log error but don't fail the delete operation
          console.warn("Error removing file entry from AssignDocument.json:", removeError);
        }

        setUploadStatus({
          success: true,
          message: result.message || `Successfully deleted ${fileName}`
        });
        // Refresh the document list and signoff data
        await Promise.all([loadDocs(), loadSignoffData(), loadAssignDocumentData()]);
        // Clear status message after 3 seconds
        setTimeout(() => {
          setUploadStatus(null);
        }, 3000);
      } else {
        setUploadStatus({
          success: false,
          message: result?.error || "Failed to delete file"
        });
      }
    } catch (e: any) {
      setUploadStatus({
        success: false,
        message: e?.message || "Unexpected error during deletion"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedDocNames.size === 0) return;
    const count = selectedDocNames.size;
    const names = Array.from(selectedDocNames);
    if (!confirm(`Are you sure you want to delete ${count} selected file(s)? This will move them to the recycle bin.`)) {
      return;
    }
    setDeletingSelected(true);
    let successCount = 0;
    let failCount = 0;
    try {
      for (const fileName of names) {
        try {
          const result = await (window as any)?.sharePointAPI?.deleteDocumentLibrary?.(fileName);
          if (result?.success) {
            successCount++;
            try {
              await (window as any)?.sharePointAPI?.removeAssignDocument?.(fileName);
            } catch {
              // ignore
            }
          } else {
            failCount++;
          }
        } catch {
          failCount++;
        }
      }
      setSelectedDocNames(new Set());
      await Promise.all([loadDocs(), loadSignoffData(), loadAssignDocumentData()]);
      if (failCount === 0) {
        setUploadStatus({ success: true, message: `Successfully deleted ${successCount} file(s).` });
      } else {
        setUploadStatus({ success: false, message: `Deleted ${successCount} file(s). ${failCount} failed.` });
      }
      setTimeout(() => setUploadStatus(null), 3000);
    } catch (e: any) {
      setUploadStatus({ success: false, message: e?.message || "Error during deletion" });
      setTimeout(() => setUploadStatus(null), 3000);
    } finally {
      setDeletingSelected(false);
    }
  };

  const handleFileClick = async (url: string) => {
    if (url) {
      // Use Electron's shell.openExternal to open in default browser
      if ((window as any).electronWindow?.openExternal) {
        try {
          await (window as any).electronWindow.openExternal(url);
        } catch (error) {
          console.error("Error opening external URL:", error);
          // Fallback to window.open if Electron API fails
          window.open(url, "_blank", "noopener,noreferrer");
        }
      } else {
        // Fallback to window.open if Electron API is not available
        window.open(url, "_blank", "noopener,noreferrer");
      }
    }
  };

  // Helper function to determine area by checking db.json
  const determineFileArea = async (fileName: string): Promise<string> => {
    try {
      // Try to load db.json and check which area contains the file
      // For now, default to "client" since loadAllDocuments loads from client folder
      // In the future, we could enhance this to check all areas
      return "client";
    } catch {
      return "client"; // Default fallback
    }
  };

  const handleRenameClick = async (fileName: string) => {
    const area = await determineFileArea(fileName);
    setFileToRename({ name: fileName, area });
    setRenameDialogOpen(true);
  };

  const handleRename = async (newName: string) => {
    if (!fileToRename) return;

    try {
      setLoading(true);
      setError("");
      const result = await (window as any)?.sharePointAPI?.renameFile?.({
        oldName: fileToRename.name,
        newName: newName,
        area: fileToRename.area,
      });

      if (result?.success) {
        setUploadStatus({
          success: true,
          message: result.message || `Successfully renamed ${fileToRename.name} to ${result.data?.new_name}`
        });
        // Refresh the document list
        await Promise.all([loadDocs(), loadSignoffData(), loadAssignDocumentData()]);
        setTimeout(() => {
          setUploadStatus(null);
        }, 3000);
      } else {
        throw new Error(result?.error || "Failed to rename file");
      }
    } catch (e: any) {
      throw new Error(e?.message || "Unexpected error during rename");
    } finally {
      setLoading(false);
    }
  };

  const handleReplaceClick = async (fileName: string) => {
    const area = await determineFileArea(fileName);
    setReplacingFile({ name: fileName, area });
    
    try {
      // Open file picker
      const result = await (window as any)?.sharePointAPI?.openFileDialog?.();
      
      if (!result?.success || !result?.filePath) {
        setReplacingFile(null);
        return;
      }

      const filePath = result.filePath;
      
      // Replace the file
      setLoading(true);
      setError("");
      const replaceResult = await (window as any)?.sharePointAPI?.replaceFile?.({
        filePath: filePath,
        fileName: fileName,
        area: area,
      });

      if (replaceResult?.success) {
        setUploadStatus({
          success: true,
          message: replaceResult.message || `Successfully replaced ${fileName}`
        });
        // Refresh the document list
        await Promise.all([loadDocs(), loadSignoffData(), loadAssignDocumentData()]);
        setTimeout(() => {
          setUploadStatus(null);
        }, 3000);
      } else {
        setUploadStatus({
          success: false,
          message: replaceResult?.error || "Failed to replace file"
        });
        setTimeout(() => {
          setUploadStatus(null);
        }, 3000);
      }
    } catch (e: any) {
      setUploadStatus({
        success: false,
        message: e?.message || "Unexpected error during replace"
      });
      setTimeout(() => {
        setUploadStatus(null);
      }, 3000);
    } finally {
      setLoading(false);
      setReplacingFile(null);
    }
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadStatus(null);
    setError("");

    try {
      // Try to get file path first (works in Electron)
      const filePath = (file as any).path;
      
      let result;
      if (filePath) {
        // Use file path directly if available
        result = await (window as any)?.sharePointAPI?.uploadDocumentLibrary?.(filePath);
      } else {
        // Fallback to base64 if path is not available
        const base64Content = await fileToBase64(file);
        result = await (window as any)?.sharePointAPI?.uploadDocumentLibrary?.({
          fileContent: base64Content,
          fileName: file.name
        });
      }
      
      if (result?.success) {
        setUploadStatus({
          success: true,
          message: `Successfully uploaded ${file.name}`
        });
        // Refresh the document list and signoff data
        await Promise.all([loadDocs(), loadSignoffData(), loadAssignDocumentData()]);
        // Clear the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        // Clear status message after 3 seconds
        setTimeout(() => {
          setUploadStatus(null);
        }, 3000);
      } else {
        setUploadStatus({
          success: false,
          message: result?.error || "Failed to upload file"
        });
      }
    } catch (e: any) {
      setUploadStatus({
        success: false,
        message: e?.message || "Unexpected error during upload"
      });
    } finally {
      setUploading(false);
    }
  };

  // Use external loading state if provided, otherwise use internal loading
  const effectiveLoading = externalLoading !== undefined ? externalLoading : loading;

  const uniqueDocs = docs.filter((doc, index, self) =>
    index === self.findIndex((d) => d.name === doc.name)
  );

  return (
    <div className="space-y-6">
      {!hideHeader && (
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-white">{title}</h2>
            <p className="text-sm text-white/60">
              {description}
            </p>
          </div>
        </div>
      )}

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-white">Documents</h3>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {uniqueDocs.length} document(s)
            </Badge>
            <input
              ref={fileInputRef}
              type="file"
              multiple={false}
              accept="*"
              onChange={handleFileSelect}
              className="hidden"
              id="document-library-upload"
            />
            {!isArchivalMode && showUploadButton && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="border-white/10 text-white/80 hover:bg-white/10 hover:text-white"
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <FolderOpen className="h-4 w-4 mr-2" />
                    Upload Files
                  </>
                )}
              </Button>
            )}
            {!isArchivalMode && selectedDocNames.size > 0 && (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={loadingSignoff}
                      className="border-blue-500/50 text-blue-300 hover:bg-blue-500/10 hover:text-blue-200"
                    >
                      {loadingSignoff ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <FileCheck className="h-4 w-4 mr-2" />
                      )}
                      Sign off on selected ({selectedDocNames.size})
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-gray-900 border-white/10 text-white min-w-[200px]" align="end">
                    {(() => {
                      const selected = Array.from(selectedDocNames);
                      const allHavePrep = selected.length > 0 && selected.every((name) => isPrepSoCompleted(getSignoffForFile(name)));
                      const canPrep = canSignoffSection("prep_so");
                      const canRev = canSignoffSection("rev_so") && allHavePrep;
                      const canConc = canSignoffSection("conc_so") && allHavePrep;
                      const canEqr = canSignoffSection("eqr_so") && allHavePrep;
                      const canPartner = canSignoffSection("partner_so") && allHavePrep;
                      return (
                        <>
                          <DropdownMenuItem
                            className={canPrep ? "cursor-pointer" : "cursor-not-allowed opacity-50"}
                            disabled={!canPrep}
                            onClick={() => canPrep && handleBulkSignoff("prep_so")}
                          >
                            Preparer
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className={canRev ? "cursor-pointer" : "cursor-not-allowed opacity-50"}
                            disabled={!canRev}
                            onClick={() => canRev && handleBulkSignoff("rev_so")}
                          >
                            Reviewer
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className={canConc ? "cursor-pointer" : "cursor-not-allowed opacity-50"}
                            disabled={!canConc}
                            onClick={() => canConc && handleBulkSignoff("conc_so")}
                          >
                            Concurrent
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className={canEqr ? "cursor-pointer" : "cursor-not-allowed opacity-50"}
                            disabled={!canEqr}
                            onClick={() => canEqr && handleBulkSignoff("eqr_so")}
                          >
                            EQR
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className={canPartner ? "cursor-pointer" : "cursor-not-allowed opacity-50"}
                            disabled={!canPartner}
                            onClick={() => canPartner && handleBulkSignoff("partner_so")}
                          >
                            Partner
                          </DropdownMenuItem>
                        </>
                      );
                    })()}
                  </DropdownMenuContent>
                </DropdownMenu>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={loadingAssignDocument || teamMembers.length === 0}
                      className="border-white/10 text-white/80 hover:bg-white/10 hover:text-white"
                    >
                      {loadingAssignDocument ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <UserPlus className="h-4 w-4 mr-2" />
                      )}
                      Assign selected ({selectedDocNames.size})
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-gray-900 border-white/10 text-white min-w-[200px] max-h-64 overflow-y-auto" align="end">
                    {loadingTeamMembers ? (
                      <div className="p-2 text-center text-white/60 text-sm">
                        <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                      </div>
                    ) : teamMembers.length === 0 ? (
                      <div className="p-2 text-center text-white/60 text-sm">
                        No team members available
                      </div>
                    ) : (
                      teamMembers.map((member) => (
                        <DropdownMenuItem
                          key={member.id}
                          className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer"
                          onClick={() => handleBulkAssignDocument(member.user_name || member.email || "")}
                          disabled={loadingAssignDocument}
                        >
                          <div className="flex flex-col">
                            <span>{member.user_name || member.email}</span>
                            {member.email && member.user_name && (
                              <span className="text-xs text-white/60">{member.email}</span>
                            )}
                          </div>
                        </DropdownMenuItem>
                      ))
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={deletingSelected}
                  onClick={handleDeleteSelected}
                  className="border-red-500/50 text-red-300 hover:bg-red-500/10 hover:text-red-200"
                >
                  {deletingSelected ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-2" />
                  )}
                  Delete selected ({selectedDocNames.size})
                </Button>
              </>
            )}
          </div>
        </div>

        {uploadStatus && (
          <div className={`p-3 rounded-md border ${
            uploadStatus.success 
              ? "border-green-500/20 bg-green-500/10 text-green-300" 
              : "border-red-500/20 bg-red-500/10 text-red-300"
          }`}>
            <div className="flex items-center gap-2">
              {uploadStatus.success ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : (
                <AlertCircle className="h-4 w-4" />
              )}
              <span className="text-sm">{uploadStatus.message}</span>
            </div>
          </div>
        )}

        {effectiveLoading ? (
          <div className="text-center text-white/60 py-8">
            Loading...
          </div>
        ) : error ? (
          <div className="text-sm text-red-400">{error}</div>
        ) : uniqueDocs.length === 0 ? (
          <Card className="border-white/10 bg-white/5 text-white">
            <CardContent className="p-6">
              <div className="text-center text-white/60">
                <FileText className="mx-auto h-12 w-12 mb-4 text-white/40" />
                <p>No documents found.</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {/* Header row with column headings (same as Control Library) */}
            {!loadingSignoff && (
              <div className="flex items-center gap-4 rounded-md border border-white/10 bg-white/5 px-3 py-2">
                <div className="w-10 shrink-0 flex items-center justify-center">
                  {!isArchivalMode && (
                    <Checkbox
                      checked={uniqueDocs.length > 0 && uniqueDocs.every((d) => selectedDocNames.has(d.name))}
                      onCheckedChange={selectAllDocs}
                      aria-label="Select all documents"
                    />
                  )}
                </div>
                <div className="flex-1 min-w-0 text-xs font-semibold text-white/70">
                  Document
                </div>
                <div className="w-24 shrink-0 text-center text-xs font-semibold text-white/70" title="Preparer">Preparer</div>
                <div className="w-24 shrink-0 text-center text-xs font-semibold text-white/70" title="Reviewer">Reviewer</div>
                <div className="w-24 shrink-0 text-center text-xs font-semibold text-white/70" title="Concurrent">Concurrent</div>
                <div className="w-24 shrink-0 text-center text-xs font-semibold text-white/70" title="EQR">EQR</div>
                <div className="w-24 shrink-0 text-center text-xs font-semibold text-white/70" title="Partner">Partner</div>
                <div className="w-20 shrink-0" />
              </div>
            )}
            <div className="space-y-2">
              {uniqueDocs.map((d, idx) => {
                const signoffEntry = getSignoffForFile(d.name);
                const hasPrepSo = signoffEntry && signoffEntry.prep_so && signoffEntry.prep_so.some(entry => entry.name && entry.name.trim() !== "");
                const hasRevSo = signoffEntry && signoffEntry.rev_so && signoffEntry.rev_so.some(entry => entry.name && entry.name.trim() !== "");
                const hasConcSo = signoffEntry && signoffEntry.conc_so && signoffEntry.conc_so.some(entry => entry.name && entry.name.trim() !== "");
                const hasEqrSo = signoffEntry && signoffEntry.eqr_so && signoffEntry.eqr_so.some(entry => entry.name && entry.name.trim() !== "");
                const hasPartnerSo = signoffEntry && signoffEntry.partner_so && signoffEntry.partner_so.some(entry => entry.name && entry.name.trim() !== "");
                const prepSoCompleted = isPrepSoCompleted(signoffEntry);
                const canSignoffPrep = canSignoffSection('prep_so');
                const canSignoffRev = canSignoffSection('rev_so') && prepSoCompleted;
                const canSignoffConc = canSignoffSection('conc_so') && prepSoCompleted;
                const canSignoffEqr = canSignoffSection('eqr_so') && prepSoCompleted;
                const canSignoffPartner = canSignoffSection('partner_so') && prepSoCompleted;

                return (
                  <div
                    key={`${d.name}-${idx}`}
                    className="flex items-center gap-4 rounded-md border border-white/10 bg-white/5 px-3 py-2 hover:border-white/20 hover:bg-white/10"
                  >
                    {!isArchivalMode && (
                      <div className="w-10 shrink-0 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedDocNames.has(d.name)}
                          onCheckedChange={(checked) => toggleDocSelection(d.name)}
                          aria-label={`Select ${d.name}`}
                        />
                      </div>
                    )}
                    {!isArchivalMode && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => e.stopPropagation()}
                            className="h-8 w-8 p-0 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 shrink-0"
                            title="File options"
                          >
                            <Menu className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-gray-900 border-white/10 text-white" align="start">
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRenameClick(d.name);
                            }}
                            className="hover:bg-white/10 cursor-pointer"
                          >
                            <FileText className="h-4 w-4 mr-2" />
                            Rename
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReplaceClick(d.name);
                            }}
                            className="hover:bg-white/10 cursor-pointer"
                            disabled={loading}
                          >
                            <Upload className="h-4 w-4 mr-2" />
                            Replace
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                    <button
                      type="button"
                      className="flex-1 min-w-0 text-left text-sm text-blue-300 hover:underline cursor-pointer truncate"
                      onClick={() => handleFileClick(d.url)}
                    >
                      {d.name}
                    </button>
                    {!loadingSignoff && (
                      <>
                        {isArchivalMode ? (
                          <>
                            <div className="w-24 shrink-0 flex justify-center">
                              <StaticSignoffGroup label="Preparer" entries={signoffEntry?.prep_so || []} />
                            </div>
                            <div className="w-24 shrink-0 flex justify-center">
                              <StaticSignoffGroup label="Reviewer" entries={signoffEntry?.rev_so || []} />
                            </div>
                            <div className="w-24 shrink-0 flex justify-center">
                              <StaticSignoffGroup label="Concurrent" entries={signoffEntry?.conc_so || []} />
                            </div>
                            <div className="w-24 shrink-0 flex justify-center">
                              <StaticSignoffGroup label="EQR" entries={signoffEntry?.eqr_so || []} />
                            </div>
                            <div className="w-24 shrink-0 flex justify-center">
                              <StaticSignoffGroup label="Partner" entries={signoffEntry?.partner_so || []} />
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="w-24 shrink-0 flex justify-center">
                              <SignoffBadge
                                label="Preparer"
                                signoffArray={signoffEntry?.prep_so || []}
                                isEmpty={!hasPrepSo}
                                onSignoffClick={() => handleSignoff(d.name, 'prep_so')}
                                disabled={!canSignoffPrep}
                                section="prep_so"
                              />
                            </div>
                            <div className="w-24 shrink-0 flex justify-center">
                              <SignoffBadge
                                label="Reviewer"
                                signoffArray={signoffEntry?.rev_so || []}
                                isEmpty={!hasRevSo}
                                onSignoffClick={() => handleSignoff(d.name, 'rev_so')}
                                disabled={!canSignoffRev}
                                section="rev_so"
                              />
                            </div>
                            <div className="w-24 shrink-0 flex justify-center">
                              <SignoffBadge
                                label="Concurrent"
                                signoffArray={signoffEntry?.conc_so || []}
                                isEmpty={!hasConcSo}
                                onSignoffClick={() => handleSignoff(d.name, 'conc_so')}
                                disabled={!canSignoffConc}
                                section="conc_so"
                              />
                            </div>
                            <div className="w-24 shrink-0 flex justify-center">
                              <SignoffBadge
                                label="EQR"
                                signoffArray={signoffEntry?.eqr_so || []}
                                isEmpty={!hasEqrSo}
                                onSignoffClick={() => handleSignoff(d.name, 'eqr_so')}
                                disabled={!canSignoffEqr}
                                section="eqr_so"
                              />
                            </div>
                            <div className="w-24 shrink-0 flex justify-center">
                              <SignoffBadge
                                label="Partner"
                                signoffArray={signoffEntry?.partner_so || []}
                                isEmpty={!hasPartnerSo}
                                onSignoffClick={() => handleSignoff(d.name, 'partner_so')}
                                disabled={!canSignoffPartner}
                                section="partner_so"
                              />
                            </div>
                          </>
                        )}
                      </>
                    )}
                    {!isArchivalMode && (
                      <div className="w-20 shrink-0 flex items-center justify-end gap-2">
                            {/* Assign Document Button with Dropdown */}
                            <AssignDocumentBadge
                              assignedUsers={getAssignedUsersForFile(d.name)}
                              disabled={loadingTeamMembers || teamMembers.length === 0}
                              loading={loadingAssignDocument}
                            >
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-8 w-8 p-0 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 relative"
                                    disabled={loadingTeamMembers || teamMembers.length === 0 || loadingAssignDocument}
                                  >
                                    <UserPlus className="h-4 w-4" />
                                    {getAssignedUsersForFile(d.name).length > 0 && (
                                      <span className="absolute -top-1 -right-1 h-2 w-2 bg-blue-500 rounded-full"></span>
                                    )}
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="bg-gray-900 border-white/10 text-white max-h-64 overflow-y-auto">
                                  {loadingTeamMembers ? (
                                    <div className="p-2 text-center text-white/60 text-sm">
                                      <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                                    </div>
                                  ) : teamMembers.length === 0 ? (
                                    <div className="p-2 text-center text-white/60 text-sm">
                                      No team members available
                                    </div>
                                  ) : (
                                    teamMembers.map((member) => (
                                      <DropdownMenuItem
                                        key={member.id}
                                        className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer"
                                        onClick={() => handleAssignDocument(d.name, member.user_name || member.email || '')}
                                        disabled={loadingAssignDocument}
                                      >
                                        <div className="flex flex-col">
                                          <span>{member.user_name || member.email}</span>
                                          {member.email && member.user_name && (
                                            <span className="text-xs text-white/60">{member.email}</span>
                                          )}
                                        </div>
                                      </DropdownMenuItem>
                                    ))
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </AssignDocumentBadge>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(d.name)}
                              className="h-8 w-8 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {fileToRename && (
        <RenameFileDialog
          isOpen={renameDialogOpen}
          onClose={() => {
            setRenameDialogOpen(false);
            setFileToRename(null);
          }}
          fileName={fileToRename.name}
          onRename={handleRename}
        />
      )}
    </div>
  );
}
